import { ReactElement, forwardRef } from 'react';
import cn from 'classnames';
import { Icon, IconWeight } from '@phosphor-icons/react';

import styles from './IconButton.module.scss';

export type IconButtonType =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'outlineDark'
  | 'transparent';
export type IconButtonSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
export type IconButtonShape = 'circular' | 'square';

export interface IconButtonProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement>,
    'type'
  > {
  icon: Icon;
  type?: IconButtonType;
  size?: IconButtonSize;
  shape?: IconButtonShape;
  iconSize?: number;
  iconWeight?: IconWeight;
  className?: string;
  disabled?: boolean;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => void;
  buttonType?: 'button' | 'submit' | 'reset';
  'data-testid'?: string;
  'aria-label': string;
  children?: React.ReactNode;
  href?: string;
}

/**
 * IconButton component for action-oriented buttons using only icons
 * @param icon - Phosphor icon component
 * @param type - Visual type: 'primary', 'secondary', 'outline', 'outlineDark', or 'transparent'
 * @param size - Size variant: 'sm', 'md', 'lg', 'xl', '2xl', or '3xl'
 * @param shape - Button shape: 'circular' (default) or 'square'
 * @param iconSize - Optional custom icon size (defaults based on button size)
 * @param iconWeight - Optional icon weight: 'thin', 'light', 'regular', 'bold', 'fill', or 'duotone' (default: 'regular')
 * @param aria-label - Required accessible name describing the button action
 */
const IconButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  IconButtonProps
>(
  (
    {
      icon: IconComponent,
      type = 'primary',
      size = 'md',
      shape = 'circular',
      iconSize,
      iconWeight = 'regular',
      className,
      disabled = false,
      onClick,
      buttonType = 'button',
      'data-testid': dataTestId,
      'aria-label': ariaLabel,
      children,
      href,
      ...rest
    },
    ref
  ): ReactElement => {
    // Default icon sizes based on button size
    const defaultIconSizes: Record<IconButtonSize, number> = {
      sm: 16,
      md: 18,
      lg: 20,
      xl: 24,
      '2xl': 28,
      '3xl': 32,
    };
    const effectiveIconSize = iconSize || defaultIconSizes[size];

    // Map size values to valid CSS class names (CSS classes can't start with numbers)
    const sizeClassMap: Record<IconButtonSize, string> = {
      sm: 'sm',
      md: 'md',
      lg: 'lg',
      xl: 'xl',
      '2xl': 'size2xl',
      '3xl': 'size3xl',
    };

    const rootClassName = cn(
      styles.iconButton,
      styles[type],
      styles[sizeClassMap[size]],
      styles[shape],
      {
        [styles.disabled]: disabled,
      },
      className
    );

    const ComponentType = href ? 'a' : 'button';
    const isAnchor = ComponentType === 'a';

    return (
      <ComponentType
        {...rest}
        ref={ref as React.Ref<HTMLButtonElement> & React.Ref<HTMLAnchorElement>}
        type={href ? undefined : buttonType}
        href={isAnchor && disabled ? undefined : href || undefined}
        className={rootClassName}
        onClick={ComponentType === 'button' ? onClick : undefined}
        disabled={isAnchor ? undefined : disabled}
        aria-disabled={isAnchor ? disabled : undefined}
        tabIndex={isAnchor && disabled ? -1 : rest.tabIndex}
        data-testid={dataTestId}
        aria-label={ariaLabel}
      >
        <span className={styles.iconWrapper}>
          <IconComponent
            size={effectiveIconSize}
            weight={iconWeight}
            aria-hidden="true"
          />
          {children}
        </span>
      </ComponentType>
    );
  }
);

IconButton.displayName = 'IconButton';

export default IconButton;
